import { useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { seedDatabase } from './db/seed';
import { startSyncLoop, stopSyncLoop } from './sync/engine';
import PinLock from './components/PinLock';
import AppShell from './components/AppShell';
import Dashboard from './screens/Dashboard';
import NewSubscription from './screens/NewSubscription';
import WalkIn from './screens/WalkIn';
import WalkInHistory from './screens/WalkInHistory';
import Members from './screens/Members';
import MemberProfile from './screens/MemberProfile';
import Renewals from './screens/Renewals';
import RenewSubscription from './screens/RenewSubscription';
import Settings from './screens/Settings';

/**
 * Load the review fixture when the URL asks for it.
 *
 * `import.meta.env.DEV` is replaced with `false` at build time, so this whole
 * branch — and the dynamic import inside it — is eliminated from a production
 * bundle. The demo module is never shipped to the front desk PC.
 *
 *   ?demo        replace everything with the review data
 *   ?demo=clear  wipe it again
 *
 * It runs before the app reports ready, so the PIN screen sees the PINs the
 * fixture installs rather than an empty database.
 */
async function maybeSeedDemoData(): Promise<void> {
  if (!import.meta.env.DEV) return;

  const mode = new URLSearchParams(window.location.search).get('demo');
  if (mode === null) return;

  const demo = await import('./db/demo');
  if (mode === 'clear') {
    await demo.clearDemoData();
    console.info('[demo] cleared');
    return;
  }

  const summary = await demo.seedDemoData();
  console.info('[demo] seeded', summary, {
    staffPin: demo.DEMO_STAFF_PIN,
    managerPin: demo.DEMO_MANAGER_PIN,
  });
}

/**
 * Boot order: open and seed the local database, then gate on the PIN, then
 * render the shell. Nothing here touches the network — the app must reach a
 * usable state with the cable unplugged.
 */
export default function FrontDeskApp() {
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    seedDatabase()
      .then(maybeSeedDemoData)
      .then(() => {
        setReady(true);
        // Background sync. Never blocks boot — if there is no cloud configured
        // or no internet, it simply reports that and the app carries on.
        startSyncLoop();
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));

    return () => stopSyncLoop();
  }, []);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink p-8">
        <div className="card max-w-lg p-8">
          <h1 className="mb-3 text-2xl text-brand">Could not open the local database</h1>
          <p className="mb-4 text-white/60">
            The app stores everything in this browser. If this is a private window, or storage
            is blocked, it cannot start.
          </p>
          <pre className="overflow-x-auto rounded-btn bg-black p-4 text-sm text-status-expired">
            {error}
          </pre>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <img src="/logo.png" alt="" className="h-20 w-auto animate-pulse" />
      </div>
    );
  }

  if (!unlocked) return <PinLock onUnlock={() => setUnlocked(true)} />;

  return (
    <AppShell onLock={() => setUnlocked(false)}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/new" element={<NewSubscription />} />
        <Route path="/walk-in" element={<WalkIn />} />
        <Route path="/walk-ins" element={<WalkInHistory />} />
        <Route path="/members" element={<Members />} />
        <Route path="/members/:id" element={<MemberProfile />} />
        <Route path="/renewals" element={<Renewals />} />
        <Route path="/renew/:subscriptionId" element={<RenewSubscription />} />
        <Route path="/settings" element={<Settings />} />
      </Routes>
    </AppShell>
  );
}

